import React from 'react'
import { Button, Stack } from '@mui/material'
import { ActionCard } from './ActionCard'
import { AssistantChartList } from './AssistantChartCard'
import { AssistantImageList } from './AssistantImageCard'
import { AssistantPreviewList } from './AssistantPreviewCard'
import { AssistantTableList } from './AssistantTableCard'
import { GraybookCardList } from './GraybookCard'
import type {
  AssistantChartType,
  AssistantChatMessage,
  ProposedAction,
} from './useAssistantChat'

export function messageHasWork(message: AssistantChatMessage): boolean {
  return Boolean(
    message.charts?.length ||
      message.tables?.length ||
      message.previews?.length ||
      message.suggestions?.length ||
      message.graybookCards?.length ||
      message.imageCards?.length ||
      message.proposedActions?.some(
        action =>
          action &&
          typeof action.type === 'string' &&
          action.type !== 'createRoutine' &&
          action.type !== 'runRoutine',
      ),
  )
}

interface AssistantMessageExtrasProps {
  message: AssistantChatMessage
  dense?: boolean
  tallCharts?: boolean
  dismissedActions: Set<string>
  busyActionKey: string | null
  actionKey: (messageId: string, action: ProposedAction, index: number) => string
  onConfirm: (action: ProposedAction, messageId: string, index: number) => void
  onDismiss: (key: string) => void
  onPickChartType: (type: AssistantChartType) => void
  onOpenPath: (path: string) => void
  onSuggest: (prompt: string) => void
  suggestDisabled?: boolean
  /** Stage already shows this message’s charts. */
  hideCharts?: boolean
  /** Limit which confirm cards render. Routine forms stay in the chat. */
  actions?: 'all' | 'routine' | 'exceptRoutine'
  isChartPinned?: (index: number) => boolean
  onToggleChartPin?: (index: number) => void
}

export const AssistantMessageExtras: React.FC<AssistantMessageExtrasProps> = ({
  message,
  dense = false,
  tallCharts = false,
  dismissedActions,
  busyActionKey,
  actionKey,
  onConfirm,
  onDismiss,
  onPickChartType,
  onOpenPath,
  onSuggest,
  suggestDisabled = false,
  hideCharts = false,
  actions = 'all',
  isChartPinned,
  onToggleChartPin,
}) => {
  const actionCards = message.proposedActions?.map((action, index) => {
    if (!action || typeof action.type !== 'string') return null
    if (action.type === 'createRoutine') return null
    if (actions === 'routine' && action.type !== 'runRoutine') return null
    if (actions === 'exceptRoutine' && action.type === 'runRoutine') return null
    const key = actionKey(message.id, action, index)
    if (dismissedActions.has(key)) return null
    return (
      <ActionCard
        key={key}
        action={action}
        dense={dense}
        busy={busyActionKey === key}
        onConfirm={next => onConfirm(next, message.id, index)}
        onCancel={() => onDismiss(key)}
      />
    )
  })

  if (actions === 'routine') return <>{actionCards}</>

  return (
  <>
    {message.graybookCards && message.graybookCards.length > 0 ? (
      <GraybookCardList cards={message.graybookCards} messageId={message.id} />
    ) : null}
    {!hideCharts && message.charts && message.charts.length > 0 ? (
      <AssistantChartList
        charts={message.charts}
        onPickType={onPickChartType}
        tall={tallCharts}
        isPinned={isChartPinned}
        onTogglePin={onToggleChartPin}
      />
    ) : null}
    {message.tables && message.tables.length > 0 ? (
      <AssistantTableList tables={message.tables} onOpenPath={onOpenPath} />
    ) : null}
    {message.previews && message.previews.length > 0 ? (
      <AssistantPreviewList previews={message.previews} onOpen={onOpenPath} />
    ) : null}
    {message.imageCards && message.imageCards.length > 0 ? (
      <AssistantImageList cards={message.imageCards} />
    ) : null}
    {actionCards}
    {message.suggestions && message.suggestions.length > 0 ? (
      <Stack direction='row' flexWrap='wrap' gap={0.75} sx={{ mt: 1 }}>
        {message.suggestions.map(item => (
          <Button
            key={item.prompt}
            size='small'
            variant='outlined'
            disabled={suggestDisabled}
            onClick={() => onSuggest(item.prompt)}
            sx={{
              minHeight: 28,
              px: 1,
              py: 0.25,
              fontSize: '0.75rem',
              textTransform: 'none',
            }}
          >
            {item.label}
          </Button>
        ))}
      </Stack>
    ) : null}
  </>
  )
}
